import { useEffect, useState, useCallback } from 'react';
import {
	Box,
	Stack,
	Button,
	CardContent,
	IconButton,
	CardHeader,
	Dialog,
	LinearProgress,
	Typography,
	Grid,
} from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { useTheme } from '@mui/material/styles';

import Iconify from '../iconify';
import type {
	useGetRequestReportsQuery,
	useLazyGetRequestReportQuery,
	useUploadRequestReportMutation,
	useDeleteRequestReportMutation,
} from '../../slices/apis/requestApi';
import { Request } from '../../models';
import { useRole } from '../../hooks/useRole';
import { usePermissions } from '../../hooks/usePermissions';
import toDataUri from '../../utils/toDataUri';

interface RequestReportsProps {
	requestId: number;
	mimeTypes: string;
	isBulkRequest?: boolean;
	apis: {
		useGetRequestReportsQuery: typeof useGetRequestReportsQuery;
		useLazyGetRequestReportQuery: typeof useLazyGetRequestReportQuery;
		useUploadRequestReportMutation: typeof useUploadRequestReportMutation;
		useDeleteRequestReportMutation: typeof useDeleteRequestReportMutation;
	};
}

interface FileData {
	file: File[];
}

export const RequestReports = ({ requestId, isBulkRequest = false, apis, mimeTypes }: RequestReportsProps) => {
	const { isUser } = useRole();
	const { can } = usePermissions();
	const theme = useTheme();
	const [reportDeleteConfirmDialogOpen, setReportDeleteConfirmDialogOpen] = useState<boolean>(false);

	const [selectedReport, setSelectedReport] = useState<string | null>(null);
	const [reportTabOpened, setReportTabOpened] = useState<boolean>(false);

	const {
		useGetRequestReportsQuery,
		useLazyGetRequestReportQuery,
		useUploadRequestReportMutation,
		useDeleteRequestReportMutation,
	} = apis;

	const [uploadRequestReport, { isLoading: isUploadingReport }] = useUploadRequestReportMutation();
	const [deleteRequestReport, { isLoading: isDeletingReport, isSuccess: isDelReportSuccess }] =
		useDeleteRequestReportMutation();

	const onSubmit: SubmitHandler<FileData> = async (data) => {
		const files = data.file ?? [];

		if (files.length === 0) {
			return;
		}

		const reports = (
			await Promise.all(
				files.map(async (file) => {
					const dataUri = await toDataUri(file);

					if (!dataUri) {
						return null;
					}

					const base64Encoded = dataUri.split(',')[1];

					return {
						data: base64Encoded,
						name: file.name,
						type: file.type,
					};
				}),
			)
		).filter((report) => report !== null);

		if (reports.length === 0) {
			return;
		}

		uploadRequestReport({
			requestId,
			reports,
		});
	};

	const onDeleteReportConfirmDialogClose = () => {
		setReportDeleteConfirmDialogOpen(false);
	};

	const {
		control,
		reset,
		handleSubmit,
		formState: { isSubmitSuccessful },
	} = useForm<FileData>({
		defaultValues: {
			file: [],
		},
	});

	const { data: reports = [], isFetching: isFetchingRequestReports } = useGetRequestReportsQuery(requestId);
	const [trigger, { data, isFetching: isFetchingRequestReport }] = useLazyGetRequestReportQuery();

	useEffect(() => {
		if (isSubmitSuccessful) {
			reset({ file: [] });
		}

		if (isDelReportSuccess) {
			setReportDeleteConfirmDialogOpen(false);
		}
	}, [reset, isSubmitSuccessful, isDelReportSuccess]);

	const onReportDownload = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			setReportTabOpened(false);
			trigger({
				requestId,
				reportName: e.currentTarget.getAttribute('data-report') as string,
				action: 'download',
			});
		},
		[trigger, requestId],
	);

	const onReportPreview = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			setReportTabOpened(false);
			trigger({
				requestId,
				reportName: e.currentTarget.getAttribute('data-report') as string,
			});
		},
		[trigger, requestId],
	);

	const onDelButtonClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		setReportDeleteConfirmDialogOpen(true);
		setSelectedReport(e.currentTarget.getAttribute('data-report') as string);
	}, []);

	if (isFetchingRequestReports || isFetchingRequestReport || isUploadingReport) {
		return <LinearProgress />;
	}

	const onReportDeleteConfirm = () => {
		deleteRequestReport({
			requestId,
			reportName: selectedReport,
		});
	};

	if (data?.uri && !reportTabOpened) {
		setReportTabOpened(true);
		window.open(data.uri, '_blank');
	}

	if (isUser && reports.length === 0) {
		return (
			<>
				<CardHeader title="Reports" />
				<Box component="section" sx={{ p: 4, justifyContent: 'center', alignItems: 'center', display: 'flex' }}>
					<Typography variant="h6" color={theme.palette.grey[500]}>
						Looks like there are no reports for you to view right now.
					</Typography>
				</Box>
			</>
		);
	}

	return (
		<>
			<CardHeader title="Reports" sx={{ color: 'grey.600' }} />
			<CardContent>
				<Stack spacing={1.5}>
					{reports.map((report, index) => {
						const reportName = Request.getReportName(report.Key) ?? '';

						return (
							<Grid container key={reportName}>
								<Grid size={{ md: 10, xs: 10 }}>
									<Typography
										variant="body1"
										sx={{
											':hover': {
												cursor: 'pointer',
												color: (theme) => theme.palette.primary.main,
											},
										}}
										data-report={reportName}
										onClick={onReportPreview}
									>
										{reportName}
									</Typography>
								</Grid>

								<Grid size={{ md: 2, xs: 2 }}>
									<Stack direction="row" spacing={0.5} justifyContent="flex-end">
										<IconButton edge="end" aria-label="download" data-report={reportName} onClick={onReportDownload}>
											<Iconify
												icon={'eva:cloud-download-outline'}
												sx={{
													color: (theme) => theme.palette.primary.main,
												}}
											/>
										</IconButton>
										<IconButton edge="end" aria-label="delete" data-report={reportName} onClick={onDelButtonClick}>
											<Iconify
												icon={'eva:trash-2-outline'}
												sx={{
													color: (theme) => theme.palette.error.main,
												}}
											/>
										</IconButton>
									</Stack>
								</Grid>
							</Grid>
						);
					})}
					{can(isBulkRequest ? 'breq:report:upload' : 'req:report:upload') && (
						<Box>
							<form onSubmit={handleSubmit(onSubmit)}>
								<Controller
									control={control}
									name="file"
									render={({ field: { onChange, value }, fieldState }) => (
										<MuiFileInput
											multiple
											value={value}
											onChange={onChange}
											placeholder="Select your report"
											helperText={fieldState.invalid ? 'File is invalid' : ''}
											error={fieldState.invalid}
											InputProps={{
												inputProps: {
													accept: mimeTypes,
												},
												startAdornment: <Iconify icon={'iconamoon:attachment-light'} />,
											}}
											clearIconButtonProps={{
												title: 'Remove',
												children: <Iconify icon={'iconamoon:close-bold'} />,
											}}
										/>
									)}
								/>
								<Box>
									<Button type="submit" variant="contained" sx={{ mt: 2 }}>
										Upload
									</Button>
								</Box>
							</form>
						</Box>
					)}
				</Stack>
			</CardContent>
			{/* report delete confirm */}
			<Dialog
				open={Boolean(reportDeleteConfirmDialogOpen)}
				onClose={onDeleteReportConfirmDialogClose}
				title={'Warning'}
			>
				<Grid container spacing={2} sx={{ p: 2, justifyContent: 'center', alignItems: 'center' }}>
					<Grid size={{ md: 12 }}>
						<Typography variant="subtitle2">{`Confirm to delete report ${selectedReport}`}</Typography>
					</Grid>
					<Grid size={{ md: 4 }}>
						<Button loading={isDeletingReport} variant="outlined" color="primary" onClick={onReportDeleteConfirm}>
							<span>Confirm</span>
						</Button>
					</Grid>
					<Grid size={{ md: 4 }}>
						<Button color="error" variant="outlined" onClick={onDeleteReportConfirmDialogClose}>
							Cancel
						</Button>
					</Grid>
				</Grid>
			</Dialog>
		</>
	);
};
